import { useEffect } from 'react';

interface ReturnPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InfoRowProps {
  label: string;
  value: string;
  highlight?: boolean;
}

function InfoRow({ label, value, highlight }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between bg-[#F7F4F0] rounded-lg px-4 py-3">
      <span className="text-sm text-[#5C4A3A]">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? 'text-red-500' : 'text-[#8B7355]'}`}>{value}</span>
    </div>
  );
}

function CheckItem({ text, ok = true }: { text: string; ok?: boolean }) {
  return (
    <li className="flex items-start gap-2 text-sm text-[#5C4A3A]">
      <i className={`${ok ? 'ri-check-line text-[#8B7355]' : 'ri-close-line text-red-400'} text-base mt-0.5 flex-shrink-0`}></i>
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-6 h-6 flex items-center justify-center">
        <i className={`${icon} text-[#8B7355] text-lg`}></i>
      </div>
      <h3 className="text-base font-semibold text-[#3D2B1F]">{title}</h3>
    </div>
  );
}

export default function ReturnPolicyModal({ isOpen, onClose }: ReturnPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', handleKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKey);
      };
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#F7F4F0] px-8 pt-8 pb-6 relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[#3D2B1F] text-lg"></i>
          </button>
          <p className="text-xs font-medium tracking-widest text-[#8B7355] uppercase mb-2">Return &amp; Refund Policy</p>
          <h2 className="text-2xl font-bold text-[#3D2B1F]">교환·환불 정책</h2>
          <p className="text-xs text-[#8B7355]/70 mt-1">전자상거래 등에서의 소비자보호에 관한 법률 기준</p>
        </div>

        {/* Body */}
        <div className="px-8 py-7 max-h-[62vh] overflow-y-auto space-y-6">

          {/* 청약철회 법정 기간 */}
          <div>
            <SectionTitle icon="ri-calendar-check-line" title="청약철회 법정 기간" />
            <div className="pl-8 space-y-2">
              <InfoRow label="단순 변심 (미개봉·미사용)" value="수령일로부터 7일 이내" />
              <InfoRow label="상품 불량·오배송·표시 상이" value="사실을 안 날로부터 30일 이내" />
            </div>
            <p className="pl-8 text-xs text-[#8B7355] mt-2 leading-relaxed">
              ※ 청약철회 기간은 「전자상거래법」 제17조에 따른 법정 권리입니다. 위 기간 내에는 특별한 사유 없이 청약철회가 가능합니다.
            </p>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 교환·환불 가능 사유 */}
          <div>
            <SectionTitle icon="ri-checkbox-circle-line" title="교환·환불 가능 사유" />
            <ul className="pl-8 space-y-2">
              <CheckItem text="수령 후 7일 이내 단순 변심 (미개봉·미사용·원포장 상태)" />
              <CheckItem text="상품 자체 불량 또는 파손 (수령 후 30일 이내)" />
              <CheckItem text="주문 내용과 다른 상품 오배송 (수령 후 30일 이내)" />
              <CheckItem text="배송 중 파손·훼손된 경우" />
              <CheckItem text="표시·광고 내용과 실제 상품이 현저히 다른 경우" />
              <CheckItem text="상품 구성품 누락 (수령 즉시 확인 후 신청)" />
            </ul>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 교환·환불 불가 사유 */}
          <div>
            <SectionTitle icon="ri-close-circle-line" title="교환·환불 제한 사유" />
            <ul className="pl-8 space-y-2">
              <CheckItem ok={false} text="수령 후 7일이 경과한 단순 변심" />
              <CheckItem ok={false} text="개봉·사용·세탁 등으로 상품 가치가 현저히 감소한 경우" />
              <CheckItem ok={false} text="고객 부주의로 인한 상품 훼손·오염" />
              <CheckItem ok={false} text="상품 포장·라벨 훼손으로 재판매가 불가능한 경우" />
              <CheckItem ok={false} text="구성품 일부 분실 또는 임의 조작·개조한 경우" />
              <CheckItem ok={false} text="주문 제작·맞춤 제작 상품 (단, 불량 제외)" />
            </ul>
            <p className="pl-8 text-xs text-[#8B7355] mt-2 leading-relaxed">
              ※ 위 사유는 「전자상거래법」 제17조 제2항 청약철회 제한 규정에 근거합니다.
            </p>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 반품 배송비 */}
          <div>
            <SectionTitle icon="ri-truck-line" title="반품 배송비 기준" />
            <div className="pl-8 space-y-2">
              <InfoRow label="상품 불량·오배송·표시 상이" value="당사 전액 부담" />
              <InfoRow label="단순 변심 반품" value="고객 부담 (편도 3,000원)" />
              <InfoRow label="교환 배송비 (단순 변심)" value="왕복 6,000원 고객 부담" />
            </div>
            <p className="pl-8 text-xs text-[#8B7355] mt-2 leading-relaxed">
              ※ 도서·산간·제주 지역은 추가 배송비가 발생할 수 있습니다.
            </p>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 환불 처리 절차 */}
          <div>
            <SectionTitle icon="ri-refund-2-line" title="환불 처리 절차 및 기간" />
            <div className="pl-8 space-y-2">
              <div className="flex items-start gap-3">
                {['교환·환불 신청', '반품 상품 회수', '상품 상태 확인', '환불 처리'].map((step, i) => (
                  <div key={step} className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-7 h-7 rounded-full bg-[#8B7355]/15 flex items-center justify-center text-xs font-bold text-[#8B7355]">{i + 1}</div>
                    <span className="text-[10px] text-[#5C4A3A] text-center leading-tight">{step}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2 mt-3">
                <InfoRow label="신용카드 결제" value="승인 취소 (영업일 3–5일)" />
                <InfoRow label="계좌이체·무통장입금" value="환불 계좌로 3–5 영업일 이내" />
                <InfoRow label="적립금·쿠폰 결제" value="즉시 복원" />
              </div>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 신청 방법 */}
          <div>
            <SectionTitle icon="ri-customer-service-2-line" title="교환·환불 신청 방법" />
            <div className="pl-8 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <div className="bg-[#F7F4F0] rounded-lg px-4 py-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-[#8B7355] text-sm"></i>
                  <span>이메일: <span className="font-medium text-[#3D2B1F]">zeowithin@gmail.com</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-[#8B7355] text-sm"></i>
                  <span className="text-xs text-[#8B7355]">평일 10:00 – 17:00 (주말·공휴일 휴무)</span>
                </div>
              </div>
              <p className="text-xs text-[#8B7355]/80 leading-relaxed">
                신청 시 주문번호, 상품명, 사유, 불량 사진(해당 시)을 함께 보내주시면 신속하게 처리해 드립니다.
              </p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 소비자 분쟁 해결 */}
          <div>
            <SectionTitle icon="ri-scales-3-line" title="소비자 분쟁 해결 기준" />
            <div className="pl-8 text-xs text-[#5C4A3A] leading-relaxed space-y-1.5">
              <p>본 정책에 명시되지 않은 사항은 공정거래위원회 고시 「소비자분쟁해결기준」 및 관련 법령에 따릅니다.</p>
              <p>분쟁이 원만히 해결되지 않는 경우 아래 기관의 도움을 받으실 수 있습니다.</p>
              <ul className="mt-1 space-y-1 list-disc list-inside">
                <li>한국소비자원 소비자상담센터 (www.kca.go.kr / ☎ 1372)</li>
                <li>공정거래위원회 신고센터 (www.ftc.go.kr / ☎ 1600-4060)</li>
                <li>전자거래분쟁조정위원회 (www.ecmc.or.kr / ☎ 1661-5714)</li>
              </ul>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-[#EDE8E0]">
          <button
            onClick={onClose}
            className="w-full bg-[#8B7355] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#7A6245] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
